import { z } from 'zod/v4'
import { DataType, FieldRequirement } from 'skedyul-db/enums'
import {
  CreateFieldDefinitionSchema,
  UpdateFieldDefinitionSchema,
  GetFieldDefinitionSchema,
} from '../features/definitions/resources/types'
import {
  FieldSectionHandleSchema,
  GetFieldSectionSchema,
} from '../features/sections/resources/types'

export const RawValue = z.array(z.unknown())

export const VisibilitySchema = z.object({
  list: z.boolean().optional(),
  filters: z.boolean().optional(),
  data: z.boolean().optional(),
})

export const PermissionsSchema = z.object({
  read: z.boolean().optional(),
  write: z.boolean().optional(),
})

// Create a reusable schema for default value properties
export const DefaultValueBaseSchema = z.object({
  value: RawValue,
  optionIds: z.array(z.string()),
})

export const ValidationMessagesSchema = z.object({
  UNIQUE: z.string().optional().nullable(),
  REQUIRED: z.string().optional().nullable(),
  REGEX: z.string().optional().nullable(),
})

// AI metadata schema for controlling how AI agents interact with fields
export const AiMetaSchema = z
  .object({
    description: z.string().optional(),
    mutability: z
      .enum(['always', 'restricted', 'create_only', 'immutable'])
      .optional(),
  })
  .optional()
  .nullable()

export type AiMeta = z.infer<typeof AiMetaSchema>

// Field appearance schema for UI presentation (icons, placeholder, help text)
export const FieldAppearanceSchema = z
  .object({
    leftIcon: z.string().optional().nullable(),
    rightIcon: z.string().optional().nullable(),
    placeholder: z.string().optional().nullable(),
    helpText: z.string().optional().nullable(),
  })
  .optional()
  .nullable()

export type FieldAppearance = z.infer<typeof FieldAppearanceSchema>

// Default value schema with basic strategy options
const UpsertDefaultValueSchema = DefaultValueBaseSchema.extend({
  strategy: z
    .enum(['replace-empty', 'replace-all', 'replace-legacy'])
    .optional(),
})

// Common base fields shared between create and update schemas
const BaseFieldSchema = z.object({
  label: z.string().min(1),
  description: z.string().optional().nullable(),

  // unique validation
  isUnique: z.boolean().optional().nullable(),

  // required validation
  requirement: z
    .nativeEnum(FieldRequirement)
    .default(FieldRequirement.ON_CREATE),

  // icon (deprecated - use appearance.leftIcon instead)
  icon: z.string().optional().nullable(),

  // UI appearance settings (icons, placeholder, help text)
  appearance: FieldAppearanceSchema,

  validationMessages: ValidationMessagesSchema.optional().nullable(),

  // AI metadata for controlling how AI agents interact with this field
  aiMeta: AiMetaSchema,
})

export const CreateFieldSchema = BaseFieldSchema.extend({
  system: z.boolean().optional().nullable(),
  handle: z
    .string()
    .min(1)
    .regex(
      /^[a-zA-Z0-9_-]+$/,
      'Handle can only contain letters, numbers, underscores, and hyphens',
    ),
  type: z.nativeEnum(DataType).default('STRING'),
  isList: z.boolean().optional(),
  modelId: z.string(),
  appInstallationId: z.string().optional().nullable(),
  defaultValue: UpsertDefaultValueSchema,
  // Definition is optional for system fields (they skip definition handling)
  definition: CreateFieldDefinitionSchema.optional(),
  // Optional visibility and permissions - if not provided, defaults are used
  visibility: VisibilitySchema.optional(),
  permissions: PermissionsSchema.optional(),
  /** Workplace field section handle; find-or-create on save. Null clears. */
  sectionHandle: FieldSectionHandleSchema.nullable().optional(),
})

// Ensure one of definition or definitionId is provided
export const ValidatedCreateFieldSchema = CreateFieldSchema

export const UpdateFieldSchema = BaseFieldSchema.extend({
  id: z.string(),
  defaultValue: UpsertDefaultValueSchema,
  definition: UpdateFieldDefinitionSchema.optional().nullable(),
  appInstallationId: z.string().optional().nullable(),
  // Optional: Override type for system fields
  type: z.nativeEnum(DataType).optional(),
  // Optional: Override system flag
  system: z.boolean().optional(),
  // Optional: Override isList flag
  isList: z.boolean().optional(),
  /** Workplace field section handle; find-or-create on save. Null clears. */
  sectionHandle: FieldSectionHandleSchema.nullable().optional(),
})

// Validate update schema with uniqueness constraints
export const ValidatedUpdateFieldSchema = UpdateFieldSchema

// Import LinkedAppSchema from shared CRM types to avoid circular dependencies
import { LinkedAppSchema } from '../../../resources/shared'

// Separate schema for getting fields
export const GetFieldSchema = BaseFieldSchema.extend({
  id: z.string(),
  defaultValue: DefaultValueBaseSchema,
  system: z.boolean(),
  visibility: VisibilitySchema.optional().nullable(),
  permissions: PermissionsSchema.optional().nullable(),
  handle: z
    .string()
    .min(1)
    .regex(
      /^[a-zA-Z0-9_-]+$/,
      'Handle can only contain letters, numbers, underscores, and hyphens',
    ),
  type: z.nativeEnum(DataType),
  definition: GetFieldDefinitionSchema.optional().nullable(),
  modelId: z.string(),
  relationshipLinkId: z.string().optional().nullable(),
  requirement: z.nativeEnum(FieldRequirement),
  // Apps linked to this field via AppResourceInstance
  linkedApps: z.array(LinkedAppSchema).optional(),
  /** Visualization helper; null/omitted when the field is unsectioned. */
  section: GetFieldSectionSchema.optional().nullable(),
  /** True when no active Record has meaningful data for this field (set by getCRM). */
  unused: z.boolean().optional(),
})

export type GetField = z.infer<typeof GetFieldSchema>
